// ⚠️  GENERATED — do not edit. Run `npm run inferrex:generate` to regenerate.

// Read import.meta.env via a local cast rather than augmenting the global
// ImportMeta interface — that augmentation would conflict with vite/client's
// ImportMetaEnv declaration in any host app that pulls in both (TS2717).
const __env: Record<string, string | undefined> =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
/**
 * Microsoft Entra ID authentication provider (SaaS-on-Azure path).
 *
 * Active when VITE_AUTH_PROVIDER=entra. The Azure counterpart to the Firebase provider:
 * MSAL redirect sign-in against the SPA app registration, then a silent token for the API
 * app (audience = the backend's INFERREX_ENTRA_API_AUDIENCE). `@azure/msal-browser` is
 * dynamic-imported (same pattern as firebase/*), so non-entra builds neither bundle nor
 * require it. If VITE_ENTRA_CLIENT_ID / VITE_ENTRA_TENANT_ID are missing the provider
 * surfaces a configError rather than crashing.
 *
 * Entra owns the credential UX (sign-up, password reset, MFA) on its hosted pages — those
 * methods throw here with a clear message, matching the sovereign provider's posture.
 */

import {
  createContext, useCallback, useContext, useEffect,
  useMemo, useRef, useState,
} from 'react';
import type {
  AuthContextValue, AuthProviderProps, AuthState, AuthUser, SocialProvider,
} from './types.js';
import { captureTenantLink, WORKSPACE_SIGN_IN_PATH } from '../pending-tenant-entry.js';

// Minimal local shapes for the @azure/msal-browser bits we use — keeps non-entra builds
// (which don't have msal as a dep) compiling without the static import. Real types resolve
// at runtime via the dynamic import.
interface AccountInfo {
  readonly homeAccountId: string;
  readonly username: string;
  readonly name?: string;
  readonly idTokenClaims?: Record<string, unknown>;
}
interface AuthenticationResult {
  readonly accessToken: string;
  readonly account: AccountInfo | null;
}
interface Pca {
  initialize(): Promise<void>;
  // MSAL 5 reads this flag from the argument, not from auth.navigateToLoginRequestUrl.
  // Omit it and the default (true) sends the browser back to /login with the code still
  // unredeemed, and the login route then sits on "Redirecting to sign-in…" forever.
  handleRedirectPromise(options?: { hash?: string; navigateToLoginRequestUrl?: boolean }): Promise<AuthenticationResult | null>;
  getAllAccounts(): AccountInfo[];
  setActiveAccount(account: AccountInfo | null): void;
  getActiveAccount(): AccountInfo | null;
  loginRedirect(request: { scopes: string[]; loginHint?: string; prompt?: string }): Promise<void>;
  acquireTokenSilent(request: { scopes: string[]; account: AccountInfo }): Promise<AuthenticationResult>;
  acquireTokenRedirect(request: { scopes: string[]; account?: AccountInfo }): Promise<void>;
  logoutRedirect(request?: { account?: AccountInfo | null }): Promise<void>;
}

// Runtime config (window.__INFERREX_CONFIG__.entra, set by /config.js BEFORE this bundle) wins
// over the build-time env. Each deployment — SaaS, sovereign, every air-gapped customer's own
// Entra/IdP app registration — sets its own clientId/tenantId at deploy time via the container
// entrypoint; ONE image, no rebuild. Local dev falls back to the baked VITE_ENTRA_* default.
const __rt: Record<string, unknown> =
  typeof window !== 'undefined'
    ? ((window as unknown as { __INFERREX_CONFIG__?: Record<string, unknown> }).__INFERREX_CONFIG__ ?? {})
    : {};
const __entra = (__rt['entra'] as Record<string, string | undefined> | undefined) ?? {};
// Use || not ?? — the runtime entrypoint renders absent values as "" (empty string), which ?? would
// NOT treat as missing; || correctly falls through so an empty redirectUri → window.location.origin
// (a "" reaching MSAL's redirectUri throws "Failed to construct 'URL': Invalid URL").
const CLIENT_ID = __entra.clientId || __env['VITE_ENTRA_CLIENT_ID'];
const TENANT_ID = __entra.tenantId || __env['VITE_ENTRA_TENANT_ID'];
// Scope that yields an access token for the API app (its audience). e.g. api://<api-id>/access_as_user.
const API_SCOPE = __entra.apiScope || __env['VITE_ENTRA_API_SCOPE'] || '';
// Empty redirectUri → MSAL uses window.location.origin, so a temp/preview domain just works.
const REDIRECT_URI = __entra.redirectUri || __env['VITE_ENTRA_REDIRECT_URI']
  || (typeof window !== 'undefined' ? window.location.origin : '');
const ADMIN_ROLE = __entra.adminRole || __env['VITE_ENTRA_ADMIN_ROLE'] || 'inferrex_admin';
// B1 CIAM seam: Entra External ID issues from {tid}.ciamlogin.com — a different
// authority HOST, same tenant-id path shape. Default = workforce Entra; the CIAM
// cutover sets authorityHost (runtime config or build env), no code change.
const AUTHORITY_HOST = __entra.authorityHost || __env['VITE_ENTRA_AUTHORITY_HOST'] || 'login.microsoftonline.com';

const SCOPES = API_SCOPE ? [API_SCOPE] : ['openid', 'profile', 'email'];

const EntraAuthContext = createContext<AuthContextValue | null>(null);

async function initMsal(): Promise<Pca | null> {
  if (!CLIENT_ID || !TENANT_ID) return null;
  const msal = await import(/* @vite-ignore */ '@azure/msal-browser' as string);
  const pca = new msal.PublicClientApplication({
    auth: {
      clientId: CLIENT_ID,
      authority: `https://${AUTHORITY_HOST}/${TENANT_ID}`,
      // Non-default hosts (ciamlogin.com) must be declared or MSAL rejects the authority.
      ...(AUTHORITY_HOST === 'login.microsoftonline.com' ? {} : { knownAuthorities: [AUTHORITY_HOST] }),
      redirectUri: REDIRECT_URI,
      // MSAL 5 does not read this config flag inside handleRedirectPromise — the call
      // below passes navigateToLoginRequestUrl: false explicitly. Left here so a future
      // MSAL that does honour the config stays on the app origin too.
      navigateToLoginRequestUrl: false,
    },
    cache: { cacheLocation: 'localStorage' },
  });
  await pca.initialize();
  return pca as unknown as Pca;
}

function isAdminFromAccount(account: AccountInfo | null): boolean {
  const claims = account?.idTokenClaims ?? {};
  const roles = Array.isArray(claims['roles']) ? (claims['roles'] as unknown[]) : [];
  if (roles.includes(ADMIN_ROLE)) return true;
  const groups = Array.isArray(claims['groups']) ? (claims['groups'] as unknown[]) : [];
  const adminGroup = __entra.adminGroup || __env['VITE_ENTRA_ADMIN_GROUP'];
  return adminGroup ? groups.includes(adminGroup) : false;
}

function toAuthUser(account: AccountInfo): AuthUser {
  const claims = account.idTokenClaims ?? {};
  const oid = typeof claims['oid'] === 'string' ? (claims['oid'] as string) : account.homeAccountId;
  return {
    uid: oid,
    email: account.username,
    displayName: account.name ?? account.username,
  };
}

// MSAL raises BrowserAuthError with errorCode 'interaction_in_progress' when a second interactive
// operation starts while one is already running. Match on the code, and fall back to the message
// for any build that does not surface errorCode.
function isInteractionInProgress(err: unknown): boolean {
  const code = (err as { errorCode?: unknown } | null)?.errorCode;
  if (code === 'interaction_in_progress') return true;
  const message = err instanceof Error ? err.message : '';
  return message.includes('interaction_in_progress');
}

export function EntraAuthProvider({ children }: AuthProviderProps) {
  const pcaRef = useRef<Pca | null>(null);
  const [state, setState] = useState<AuthState>({
    user: null, loading: true, token: null, configError: null, mfaPending: false, isAdmin: false,
  });
  // True while an interactive redirect is navigating away. MSAL permits ONE interactive operation
  // at a time, so a second caller must not fire acquireTokenRedirect again.
  const redirectInFlightRef = useRef(false);

  // Serialized interactive acquire. Both the bootstrap effect and getToken fall back to a redirect
  // when the silent acquire fails; without a guard the losing caller makes MSAL throw
  // 'interaction_in_progress'. That rejection escapes getToken into SSEClient.connect (fired with
  // void, so nobody catches it) and breaks the realtime stream. Here a losing caller defers quietly
  // to the redirect already running instead of throwing.
  const beginInteractiveAcquire = useCallback(async (account: AccountInfo): Promise<void> => {
    const pca = pcaRef.current;
    if (!pca) return;
    if (redirectInFlightRef.current) return;
    redirectInFlightRef.current = true;
    try {
      await pca.acquireTokenRedirect({ scopes: SCOPES, account });
    } catch (err) {
      // The redirect did not navigate away — clear the flag so a later attempt can retry.
      redirectInFlightRef.current = false;
      if (!isInteractionInProgress(err)) throw err;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const pca = await initMsal();
        if (cancelled) return;
        if (!pca) {
          setState({
            user: null, loading: false, token: null, mfaPending: false, isAdmin: false,
            configError: 'VITE_ENTRA_CLIENT_ID / VITE_ENTRA_TENANT_ID not set. Configure the Entra SPA app registration.',
          });
          return;
        }
        pcaRef.current = pca;
        // Tenant-scoped sign-in (2026-10-10): a tenant link (/t/<tenant id>) names the tenant this
        // session is for. Write it down BEFORE anything can send the browser to Microsoft (which
        // returns to the origin, not the link), and move off the path — the app has no /t/* page.
        if (typeof window !== 'undefined' && captureTenantLink(window.location.pathname)) {
          window.history.replaceState(null, '', '/');
        }
        // Catch a redirect sign-in response, if we just came back from Entra.
        // The flag MUST be passed: MSAL 5 defaults it to true and otherwise
        // navigates to the page login started on (/login) before redeeming the code.
        const redirectResult = await pca.handleRedirectPromise({ navigateToLoginRequestUrl: false });
        if (cancelled) return;
        const account = redirectResult?.account ?? pca.getAllAccounts()[0] ?? null;
        if (!account && typeof window !== 'undefined' && window.location.pathname === WORKSPACE_SIGN_IN_PATH) {
          // The one sign-in page we render ourselves: the workspace-ID box. Stay here, signed out, so
          // the AuthGate can show it; submitting it starts the same managed sign-in as below.
          setState({ user: null, loading: false, token: null, configError: null, mfaPending: false, isAdmin: false });
          return;
        }
        if (!account) {
          // No session → bounce straight to Microsoft's MANAGED sign-in (Entra-hosted). We render no
          // custom login UI; keep loading=true so the app shows the redirecting spinner, never an
          // unauthenticated screen. The user returns here authenticated (handled above on next mount).
          // prompt:'select_account' forces the account picker every time, so a stale/personal Microsoft
          // account (e.g. a consumer account sharing a work email) can never be silently SSO'd into a
          // token the API rejects — the classic "signs in, bounces back to sign-in" loop.
          await pca.loginRedirect({ scopes: SCOPES, prompt: 'select_account' });
          return;
        }
        // A 401 during boot sets inferrex.reauth and sends the browser to /login.
        // The account is already in the MSAL cache (the code was just redeemed).
        // Calling loginRedirect here is what bounced the portal straight back to
        // the Microsoft account picker. Use the cached account. Only an interactive
        // redirect when silent acquire actually fails.
        pca.setActiveAccount(account);
        let token: string | null = redirectResult?.accessToken ?? null;
        if (!token) {
          try {
            const result = await pca.acquireTokenSilent({ scopes: SCOPES, account });
            token = result.accessToken;
          } catch {
            // Silent renewal failed — the server session is gone (e.g. AADSTS160021 "user session
            // does not exist"), which happens whenever the Entra session expires. Do NOT render a
            // tokenless "authenticated" state: it 401s every API call forever with no sign-in prompt
            // (the classic stale-account trap). Force an interactive redirect so the session
            // self-heals, mirroring getToken() below. loading stays true — we navigate away.
            try { sessionStorage.removeItem('inferrex.reauth'); } catch { /* private mode */ }
            await beginInteractiveAcquire(account);
            return;
          }
        }
        // Publish the bearer before React renders. The shell's first fetches read this
        // when the api client has not been initialised yet; without it they 401 and the
        // reauth flag sends the browser back to Microsoft.
        try {
          sessionStorage.removeItem('inferrex.reauth');
          if (token) sessionStorage.setItem('inferrex.accessToken', token);
        } catch { /* private mode */ }
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        // The workspace-ID box stays usable when signed in: it is how a session names a tenant.
        const onLogin = (path === '/login' || path.startsWith('/login/')) && path !== WORKSPACE_SIGN_IN_PATH;
        if (onLogin) {
          // AuthGate only ever renders the spinner. A redeemed session has to leave /login
          // or the user watches "Redirecting to sign-in…" after Microsoft already succeeded.
          try { sessionStorage.removeItem('inferrex.reauth'); } catch { /* private mode */ }
          window.location.replace('/');
          return;
        }
        setState({
          user: toAuthUser(account), loading: false, token, configError: null,
          mfaPending: false, isAdmin: isAdminFromAccount(account),
        });
      } catch (err) {
        if (!cancelled) {
          setState({
            user: null, loading: false, token: null, mfaPending: false, isAdmin: false,
            configError: err instanceof Error ? err.message : 'Entra init failed',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [beginInteractiveAcquire]);

  const signIn = useCallback(async (email: string, _password: string) => {
    const pca = pcaRef.current;
    if (!pca) throw new Error('Entra not configured');
    // Entra is redirect-based — credentials are entered on Microsoft's hosted page,
    // never handled here. email, when given, is passed as a login hint.
    await pca.loginRedirect(email
      ? { scopes: SCOPES, loginHint: email }
      : { scopes: SCOPES, prompt: 'select_account' });
  }, []);

  const signInWithSocial = useCallback(async (_provider: SocialProvider) => {
    const pca = pcaRef.current;
    if (!pca) throw new Error('Entra not configured');
    await pca.loginRedirect({ scopes: SCOPES });
  }, []);

  const signUp = useCallback(async (_email: string, _password: string) => {
    throw new Error('Sign-up is managed in Microsoft Entra ID. Contact your administrator.');
  }, []);

  const resetPassword = useCallback(async (_email: string) => {
    throw new Error('Password reset is managed in Microsoft Entra ID (your sign-in page).');
  }, []);

  const verifyMfa = useCallback(async (_code: string) => {
    throw new Error('MFA is handled inline by Microsoft Entra ID, not as a separate challenge.');
  }, []);

  const signOut = useCallback(async () => {
    const pca = pcaRef.current;
    if (!pca) return;
    try { sessionStorage.removeItem('inferrex.accessToken'); } catch { /* private mode */ }
    await pca.logoutRedirect({ account: pca.getActiveAccount() });
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    const pca = pcaRef.current;
    const account = pca?.getActiveAccount() ?? pca?.getAllAccounts()[0] ?? null;
    if (!pca || !account) return null;
    try {
      const result = await pca.acquireTokenSilent({ scopes: SCOPES, account });
      return result.accessToken;
    } catch {
      // Silent acquire failed (interaction required) — kick off the guarded interactive redirect,
      // which returns null rather than rejecting when a redirect is already in progress.
      await beginInteractiveAcquire(account);
      return null;
    }
  }, [beginInteractiveAcquire]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signUp, signInWithSocial, signOut, resetPassword, getToken, verifyMfa }),
    [state, signIn, signUp, signInWithSocial, signOut, resetPassword, getToken, verifyMfa],
  );

  return <EntraAuthContext.Provider value={value}>{children}</EntraAuthContext.Provider>;
}

export function useEntraAuth(): AuthContextValue {
  const ctx = useContext(EntraAuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
